import { create } from 'zustand';

export const DEFAULT_FX = {
  satBase:1.0, satGain:0.7, chromaBase:0.0, chromaGain:1.4, grainBase:0.02, grainGain:0.10,
  feedGain:0.5, zoom:0.012, rot:1.0, hue:0.0, hueRate:0.0,
  pulse:0.0, pulseRate:6.0, shear:0.0, voidness:0.0,
};

export type ENTHEAState = {
  mode: number;
  dose: number;
  speed: number;
  cplx: number;
  sym: number;
  trail: number;
  palette: number;
  planform: number;
  flicker: boolean;
  fhz: number;
  journey: boolean;
  breath: boolean;
  mouse: boolean;
  asc: number;
  chaos: number;
  flips: boolean;
  math: boolean;
  midi: boolean;
  midiLearn: boolean;
  drop: boolean;
  dropFxSel: number;
  wall: number;
  wallScale: number;
  scope: boolean;
  cine: boolean;
  trans: boolean;
  substance: string | null;
  tone: number;
  audioOn: boolean;
  audioDev: string;
  audioSource: string;
  trackPlaying: boolean;
  reactivity: number;
  sensitivity: number;
  droneVol: number;
  choreo: boolean;
  entrain: boolean;
  tempoLock: boolean;
  ray: boolean;
};

/* ──── Preset Types ──── */
export interface EntheaPreset {
  name: string;
  params: Omit<ENTHEAState, 'audioOn' | 'audioDev' | 'audioSource' | 'trackPlaying'>;
  timestamp: number;
}

type AppState = ENTHEAState & {
  updateParams: (params: Partial<ENTHEAState>) => void;
  hint: string;
  setHint: (hint: string) => void;
  mathStats: string;
  setMathStats: (mathStats: string) => void;
  audioStats: { msg: string, warn: boolean };
  setAudioStats: (msg: string, warn: boolean) => void;
  tripStr: string;
  tripFill: number;
  setTripStatus: (str: string, fill: number) => void;
  /* Preset management */
  presets: EntheaPreset[];
  savePreset: (name: string) => void;
  loadPreset: (index: number) => void;
  deletePreset: (index: number) => void;
  exportPresets: () => string;
  importPresets: (json: string) => boolean;
};

const SNAPSHOT_KEYS: (keyof ENTHEAState)[] = [
  'mode','dose','speed','cplx','sym','trail','palette','planform',
  'flicker','fhz','journey','breath','mouse','asc','chaos','flips',
  'math','midi','drop','dropFxSel','wall','wallScale','scope','cine',
  'trans','substance','tone','reactivity','sensitivity','choreo',
  'entrain','tempoLock','ray',
];

function loadPresetsFromStorage(): EntheaPreset[] {
  try { return JSON.parse(localStorage.getItem('enthea_presets') || '[]'); }
  catch { return []; }
}

export const useStore = create<AppState>((set, get) => ({
  mode: 0, dose: 0.45, speed: 1.0, cplx: 0.55, sym: 6, trail: 0.35,
  palette: 0, planform: 0, flicker: false, fhz: 10.0,
  journey: true, breath: false, mouse: false,
  asc: 0.0, chaos: 0.3, flips: false, math: false, midi: false, midiLearn: false, drop: true, dropFxSel: 0, wall: 0, wallScale: 3.0, scope: false, cine: true, trans: true, substance: null, tone: 0.35,
  audioOn: false, audioDev: '', audioSource: 'mic', trackPlaying: false, reactivity: 0.6, sensitivity: 1.2, droneVol: 0.12, choreo: true, entrain: false, tempoLock: false, ray: true,
  updateParams: (params) => set((state) => ({ ...state, ...params })),
  hint: '',
  setHint: (hint) => set({ hint }),
  mathStats: '',
  setMathStats: (mathStats) => set({ mathStats }),
  audioStats: { msg: '', warn: false },
  setAudioStats: (msg, warn) => set({ audioStats: { msg, warn } }),
  tripStr: '—',
  tripFill: 0,
  setTripStatus: (str, fill) => set({ tripStr: str, tripFill: fill }),

  /* ── Preset management ── */
  presets: loadPresetsFromStorage(),

  savePreset: (name: string) => {
    const state = get();
    const params: Record<string, unknown> = {};
    for (const key of SNAPSHOT_KEYS) params[key] = state[key];
    const preset: EntheaPreset = { name, params: params as EntheaPreset['params'], timestamp: Date.now() };
    const presets = [...state.presets, preset].slice(-50); // keep last 50
    localStorage.setItem('enthea_presets', JSON.stringify(presets));
    set({ presets });
  },

  loadPreset: (index: number) => {
    const preset = get().presets[index];
    if (!preset) return;
    set({ ...preset.params });
  },

  deletePreset: (index: number) => {
    const presets = get().presets.filter((_, i) => i !== index);
    localStorage.setItem('enthea_presets', JSON.stringify(presets));
    set({ presets });
  },

  exportPresets: () => {
    return JSON.stringify(get().presets, null, 2);
  },

  importPresets: (json: string) => {
    try {
      const imported = JSON.parse(json) as EntheaPreset[];
      if (!Array.isArray(imported)) return false;
      const presets = [...get().presets, ...imported].slice(-50);
      localStorage.setItem('enthea_presets', JSON.stringify(presets));
      set({ presets });
      return true;
    } catch { return false; }
  },
}));
